import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const KilometersToMilesConverter = () => {
  const [inputData, setInputData] = useState({
    value: '',
    fromUnit: 'kilometers',
    toUnit: 'miles'
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const convertUnits = () => {
    const { value, fromUnit, toUnit } = inputData;

    // Validate input
    if (!value) {
      setError('Please enter a value to convert');
      return;
    }

    const valueNum = parseFloat(value);
    if (isNaN(valueNum)) {
      setError('Please enter a valid number');
      return;
    }

    let convertedValue;
    let formula;

    // Conversion logic
    if (fromUnit === 'kilometers' && toUnit === 'miles') {
      convertedValue = valueNum * 0.621371;
      formula = `${valueNum} kilometers × 0.621371 = ${convertedValue.toFixed(4)} miles`;
    } else if (fromUnit === 'miles' && toUnit === 'kilometers') {
      convertedValue = valueNum * 1.60934;
      formula = `${valueNum} miles × 1.60934 = ${convertedValue.toFixed(4)} kilometers`;
    } else {
      convertedValue = valueNum;
      formula = `No conversion needed, values are the same`;
    }

    // Create visualization data
    const chartData = {
      labels: ['Original', 'Converted'],
      datasets: [{
        label: 'Values',
        data: [valueNum, convertedValue],
        backgroundColor: ['rgba(54, 162, 235, 0.5)', 'rgba(255, 99, 132, 0.5)'],
        borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)'],
        borderWidth: 1
      }]
    };

    setResults({
      value: valueNum,
      convertedValue: convertedValue.toFixed(4),
      formula,
      chartData
    });
  };

  const exampleData = {
    value: 1,
    fromUnit: 'kilometers',
    toUnit: 'miles',
    convertedValue: 0.6214
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Kilometers to Miles Converter | Distance Unit Conversion</title>
          <meta name="description" content="Convert kilometers to miles and vice versa with this simple unit converter. Get accurate results with visual comparison and step-by-step explanation." />
          <meta name="keywords" content="kilometers to miles, miles to kilometers, distance converter, unit conversion, measurement converter" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">🔄</div>
            <h1 className="text-3xl font-bold text-gray-800">Kilometers to Miles Converter</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Convert kilometers to miles and vice versa with this simple unit converter. Get accurate results with visual comparison and step-by-step explanation.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Value:</label>
              <input
                type="number"
                name="value"
                value={inputData.value}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter value to convert"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Convert From:</label>
              <select
                name="fromUnit"
                value={inputData.fromUnit}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="kilometers">Kilometers</option>
                <option value="miles">Miles</option>
              </select>
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Convert To:</label>
              <select
                name="toUnit"
                value={inputData.toUnit}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="kilometers">Kilometers</option>
                <option value="miles">Miles</option>
              </select>
            </div>
          </div>

          <button
            onClick={convertUnits}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Convert
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Conversion Result</h3>
                  <p className="text-lg">Original Value: {results.value} {inputData.fromUnit}</p>
                  <p className="text-lg">Converted Value: {results.convertedValue} {inputData.toUnit}</p>
                  <p className="text-lg mt-4">Formula: {results.formula}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Unit Comparison</h3>
                  <div className="h-64 bg-white rounded-lg">
                    <Bar
                      data={results.chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Value'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Units'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Unit Comparison'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-pink-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for converting 1 kilometer to miles:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Value: {exampleData.value}</li>
                <li>Convert From: Kilometers</li>
                <li>Convert To: Miles</li>
                <li>Result: {exampleData.convertedValue}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Use the conversion factor: 1 kilometer = 0.621371 miles</li>
                <li>Multiply the value in kilometers by the conversion factor</li>
                <li>1 kilometer × 0.621371 = 0.6214 miles</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-pink-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Kilometers to Miles Converter:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Value:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the value you want to convert</li>
                </ul>
              </li>
              <li><strong>Select Units:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose the unit you're converting from</li>
                  <li>Choose the unit you're converting to</li>
                </ul>
              </li>
              <li><strong>Click Convert:</strong> The converter will calculate:
                <ul className="list-disc pl-6 mt-2">
                  <li>The converted value</li>
                  <li>A visual comparison of the units</li>
                  <li>The conversion formula used</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-pink-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>1 kilometer ≈ 0.621371 miles</li>
                <li>1 mile ≈ 1.60934 kilometers</li>
                <li>Use for quick conversions between metric and imperial systems</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Units <span className="text-pink-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              Kilometers and miles are units of length used in different measurement systems:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Kilometers:</strong> A unit of length in the metric system, equal to 1,000 meters.</li>
              <li><strong>Miles:</strong> A unit of length in the imperial and US customary systems, equal to 5,280 feet.</li>
            </ul>
            <p className="mt-4">
              These units are commonly used in:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>Travel:</strong> Measuring distances on roads and maps</li>
                <li><strong>Navigation:</strong> Aviation and maritime navigation</li>
                <li><strong>Sports:</strong> Measuring race distances</li>
                <li><strong>Everyday Life:</strong> General distance measurements</li>
              </ul>
            </p>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-pink-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Know Your System:</strong> Understand whether you need metric or imperial units for your application</li>
              <li><strong>Double-check Conversions:</strong> Especially for critical applications like navigation or construction</li>
              <li><strong>Use Consistent Units:</strong> Ensure all measurements in a project use the same system unless conversion is necessary</li>
              <li><strong>Understand Precision:</strong> Know the required precision for your application and round appropriately</li>
            </ul>
            <p className="italic mt-4">
              Note: When converting for legal or professional purposes, consult official conversion standards.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default KilometersToMilesConverter;